import { isNil, omit } from "lodash";
import { AddOn, CatalogItemRestrictionScope } from "types";

const RESTRICTION_CODES = new Set(Object.values(CatalogItemRestrictionScope).map((scope) => `${scope}_max_exceeded`));

/**
 * `items.<item>.lineItems.<templateId>.quantity` from a purchase save; a calendar change leaves out the item, and a
 * change of arrival, experience or privacy keys the whole booking `lineItems`, naming no add-on.
 */
const RESTRICTION_KEY = /^(?:items\.([^.]+)\.)?lineItems(?:\.([^.]+)\.quantity)?$/;

/**
 * The answer over a limit: `items.<item>.guests.<guestIndex>.formResponse.fields.<index>.value`, guestIndex being the
 * attendee's position in the item's `guests`. The API names each answer that raised the total over a limit; when no
 * answer did, the limit names the add-on's line item.
 */
const ANSWER_RESTRICTION_KEY = /^(?:items\.([^.]+)\.)?guests\.(\d+)\.formResponse\.fields\.(\d+)\.value$/;

export interface RestrictionErrorAnswer {
    guestIndex: number;
    fieldIndex: number;
}

interface RestrictionFieldError {
    /** The item id, or its position in the request for an item not saved yet; empty when the key names none. */
    itemKey: string;
    message: string;
    /** Set when the error names the add-on's line item. */
    templateId?: ID;
    /** Set when the error names an attendee's answer. */
    answer?: RestrictionErrorAnswer;
    /** Set for a per event limit: what the event had left for the add-on when the API measured it. */
    catalogItemOpen?: number;
}

export interface ItemRestrictionErrors {
    /** A booking or event limit's message, keyed by template id. */
    errors: Record<ID, string>;
    /** Messages naming an answer, keyed by the attendee's position in `guests`, then by the answer's position. */
    guests: Record<number, Record<number, string>>;
    /** A per event limit's catalogItemOpen by template id, which a draft save does not measure again. */
    catalogItemOpen?: Record<ID, number>;
}

const toRestrictionFieldError = (
    key: string,
    message: string,
    catalogItemOpen?: number,
): RestrictionFieldError | null => {
    const [, answerItemKey = "", guestIndex, fieldIndex] = key.match(ANSWER_RESTRICTION_KEY) ?? [];
    if (!isNil(guestIndex)) {
        return {
            itemKey: answerItemKey,
            message,
            answer: { guestIndex: Number(guestIndex), fieldIndex: Number(fieldIndex) },
        };
    }

    const match = key.match(RESTRICTION_KEY);
    if (!match) {
        return null;
    }

    const [, itemKey = "", templateId] = match;

    return { itemKey, message, templateId, catalogItemOpen };
};

interface ApiFieldError {
    message: string;
    reason?: string;
    open?: number;
}

interface ApiErrorResponse {
    response?: { data?: { field?: Record<string, ApiFieldError> } };
}

const getRestrictionFieldErrors = (error: unknown): RestrictionFieldError[] => {
    const field = (error as ApiErrorResponse | undefined)?.response?.data?.field;
    if (!field || typeof field !== "object") {
        return [];
    }

    return Object.entries(field)
        .filter(([, value]) => RESTRICTION_CODES.has(value?.reason ?? ""))
        .map(([key, value]) => toRestrictionFieldError(key, value.message, value.open))
        .filter((fieldError): fieldError is RestrictionFieldError => fieldError !== null);
};

/** Returns the first limit error's message as the API sent it. */
export const getAddOnLimitError = (error: unknown) => {
    return getRestrictionFieldErrors(error)[0]?.message;
};

/** One item's limit errors, keyed by catalog item id, or by template id when no add-ons list is given. */
export const getRestrictionErrors = (error: unknown, addOns: AddOn[] = []) => {
    const entries = addOns.flatMap((addOn) => (addOn.template?.id ? [[addOn.template.id, addOn.id] as [ID, ID]] : []));
    const catalogItemIdByTemplateId = new Map(entries);
    const byCatalogItem: Record<ID, string> = {};

    for (const { templateId, message } of getRestrictionFieldErrors(error)) {
        if (templateId) {
            byCatalogItem[catalogItemIdByTemplateId.get(templateId) ?? templateId] = message;
        }
    }

    return byCatalogItem;
};

/**
 * For a save with several items, since two items can have the same add-on. A booking limit shows on every attendee
 * who has the add-on, an error naming an answer only on that answer.
 */
export const getItemRestrictionErrors = (error: unknown) => {
    const byItem: Record<string, ItemRestrictionErrors> = {};

    for (const { itemKey, message, templateId, answer, catalogItemOpen } of getRestrictionFieldErrors(error)) {
        byItem[itemKey] ??= { errors: {}, guests: {}, catalogItemOpen: {} };
        if (answer) {
            byItem[itemKey].guests[answer.guestIndex] ??= {};
            byItem[itemKey].guests[answer.guestIndex][answer.fieldIndex] = message;
        } else if (templateId) {
            byItem[itemKey].errors[templateId] = message;
            if (!isNil(catalogItemOpen)) {
                byItem[itemKey].catalogItemOpen = { ...byItem[itemKey].catalogItemOpen, [templateId]: catalogItemOpen };
            }
        }
    }

    return byItem;
};

/** Removes an add-on's booking or event limit error, and the error on the answer, if given. */
export const omitRestrictionError = (
    entry: ItemRestrictionErrors,
    templateId?: ID,
    answer?: RestrictionErrorAnswer,
): ItemRestrictionErrors => {
    let { errors, guests } = entry;
    if (templateId) {
        errors = omit(errors, templateId);
    }
    if (answer) {
        guests = { ...guests, [answer.guestIndex]: omit(guests[answer.guestIndex], answer.fieldIndex) };
    }

    return { ...entry, errors, guests };
};

/**
 * Removes what the old event measured when an item moves to a different one. Only an event limit carries
 * catalogItemOpen, so its add-ons name the messages to drop; booking limits and errors naming an answer stay.
 */
export const omitEventRestrictionErrors = (entry: ItemRestrictionErrors): ItemRestrictionErrors => {
    const eventLimitTemplateIds = Object.keys(entry.catalogItemOpen ?? {});

    return { ...entry, errors: omit(entry.errors, eventLimitTemplateIds), catalogItemOpen: {} };
};

/** The API keys a saved item by its id and an unsaved one by its position in the request. */
export const getRestrictionErrorsKeyForItem = (
    byItem: Record<string, ItemRestrictionErrors>,
    itemId: ID | undefined,
    index: number,
) => {
    return itemId && byItem[itemId] ? itemId : String(index);
};

export const getRestrictionErrorsForItem = (
    byItem: Record<string, ItemRestrictionErrors>,
    itemId: ID | undefined,
    index: number,
): ItemRestrictionErrors | undefined => {
    return byItem[getRestrictionErrorsKeyForItem(byItem, itemId, index)];
};
